import { z } from "zod";

import { emailSchema } from "./auth";

/**
 * MÜVEKKİL TEMSİLCİSİ DAVETİ şemaları (CLOS-177 `[124]`).
 *
 * Davet, kuruluş müvekkili adına işlem yapacak GERÇEK KİŞİYİ belirli müvekkile, normalize
 * e-postaya ve önerilen temsil kapsamına bağlar. Kabul YETKİ DEĞİLDİR: yalnız doğrulanmış
 * bir sonuç kaydıdır; gerçek rol ve kapsam CLOS-178'in command'ıyla kurulur.
 */

/**
 * Sürümlü konfigürasyon (kartın AÇIK KARARI): temsil kanıtı belge türleri, doğrulama
 * seviyesi, davet süresi ve günlük limit `config/representative.json`'da yaşar — kodda
 * gömülü değildir. Bir belge türünün eklenmesi kod dalı değil sürüm değişikliğidir.
 */
export const representativeInvitationConfigSchema = z.object({
  version: z.number().int().min(1),
  inviteExpiryDays: z.number().int().min(1),
  inviteMaxPerDay: z.number().int().min(1),
  /**
   * Bugünkü seviye `email`: kabul, doğrulanmış e-posta eşleşmesine dayanır. `document`
   * seviyesi kanıt belgesi yüklemesini zorunlu kılar — o yüzey ayrı kartla gelir; seviye
   * şimdiden konfigde ki geçiş kod değişikliği olmasın.
   */
  verificationLevel: z.enum(["email", "document"]),
  evidenceDocumentTypes: z.array(z.object({ key: z.string().min(1), label: z.string().min(1) })),
  /**
   * Temsil KAPSAMI türleri (CLOS-178 `[132]` açık kararı): ilişkinin kapsam sözlüğü.
   * `service_actions` bugün yalnız tanımlı — CLOS-246 hizmet ilişkisi tüketecek.
   */
  scopeTypes: z.array(z.object({ key: z.string().min(1), label: z.string().min(1) })),
  /** Yeni temsil ilişkisinin varsayılan süresi (gün) — sınırsız temsil yok. */
  defaultDurationDays: z.number().int().min(1),
  /** Kanıt yenileme aralığı — yenileme akışı kendi kartıyla gelecek, politika hazır. */
  evidenceRenewalMonths: z.number().int().min(1),
});
export type RepresentativeInvitationConfig = z.infer<typeof representativeInvitationConfigSchema>;

export const createRepresentativeInvitationSchema = z.strictObject({
  // `emailSchema`: davet ile kabul aynı normalizasyondan geçmeli (`inviteMemberSchema`
  // ile aynı gerekçe) — aksi hâlde büyük/küçük harf farkı eşleşmeyi bozar.
  email: emailSchema,
  proposedScope: z
    .string()
    .trim()
    .min(1, "Önerilen temsil kapsamı zorunludur")
    .max(2000, "Önerilen temsil kapsamı en fazla 2000 karakter olabilir"),
  /** Konfigdeki `evidenceDocumentTypes` anahtarlarından biri; doğrulama serviste (konfig runtime yüklenir). */
  evidenceType: z.string().trim().min(1).optional(),
  /** CLOS-174 iletişim kişisi ADAYI — yalnız kaynak referansı, temsil yetkisi değil. */
  contactId: z.uuid("Geçerli bir iletişim kişisi seçilmelidir").optional(),
});
export type CreateRepresentativeInvitationInput = z.infer<
  typeof createRepresentativeInvitationSchema
>;

export const representativeInvitationTokenSchema = z.strictObject({
  token: z.string().min(1, "Davet bağlantısı geçersiz"),
});

export type RepresentativeInvitationStatus =
  "pending" | "accepted" | "rejected" | "cancelled" | "expired";

/** Sponsor bağlamındaki liste satırı — token TAŞIMAZ (token yalnız davet e-postasında). */
export type RepresentativeInvitationSummary = {
  id: string;
  clientId: string;
  email: string;
  proposedScope: string;
  evidenceType: string | null;
  contactId: string | null;
  status: RepresentativeInvitationStatus;
  expiresAt: string;
  invitedByName: string;
  createdAt: string;
};

/**
 * Token'la görüntüleme — oturumsuz uç, bilerek DAR: davetli kime çağrıldığını kaydolmadan
 * önce görmeli ama token'ı eline geçiren biri büronun iç verisini okuyamamalı. E-posta
 * MASKELİ döner; erişim kararı maskeye değil kabul anındaki tam-adres eşleşmesine dayanır.
 */
export type RepresentativeInvitationPublicView = {
  id: string;
  organizationName: string;
  clientDisplayName: string;
  email: string;
  proposedScope: string;
  expiresAt: string;
  status: RepresentativeInvitationStatus;
};

export const activateRepresentationSchema = z.strictObject({
  /** Kabul edilmiş CLOS-177 daveti — temsil ilişkisinin tek meşru kaynağı. */
  invitationId: z.uuid("Geçerli bir davet seçilmelidir"),
  /** Konfigdeki scopeTypes anahtarlarından biri (serviste doğrulanır — konfig runtime). */
  scope: z.string().trim().min(1, "Temsil kapsamı zorunludur"),
});
export type ActivateRepresentationInput = z.infer<typeof activateRepresentationSchema>;

export const updateRepresentationScopeSchema = z.strictObject({
  scope: z.string().trim().min(1, "Temsil kapsamı zorunludur"),
});

export type ClientRepresentationStatus = "active" | "suspended" | "ended";

export type ClientRepresentationView = {
  id: string;
  clientId: string;
  representativeName: string;
  scope: string;
  evidenceType: string | null;
  status: ClientRepresentationStatus;
  startsAt: string;
  endsAt: string;
  createdByName: string;
  createdAt: string;
};

/**
 * Ekranda görünen durum adları. Metin ŞEMANIN yanında yaşıyor: arayüz kendi karşılığını
 * uydurursa aynı durum iki ekranda iki farklı isimle anılır ve kullanıcı bunları iki ayrı
 * şey sanır.
 */
export const REPRESENTATIVE_INVITATION_STATUS_NAMES: Record<
  RepresentativeInvitationStatus,
  string
> = {
  pending: "Yanıt bekliyor",
  accepted: "Kabul edildi",
  rejected: "Reddedildi",
  cancelled: "İptal edildi",
  expired: "Süresi doldu",
};

export const CLIENT_REPRESENTATION_STATUS_NAMES: Record<ClientRepresentationStatus, string> = {
  active: "Aktif",
  suspended: "Askıda",
  ended: "Sona erdi",
};
